import React, { useState } from 'react';

import SummaryCard from './SummaryCard';

function YourNetworth({ executiveSummary, financialSnapshot, formatCurrency }) {
    const [showAllAssets, setShowAllAssets] = useState(false);
    const [showAllLiabilities, setShowAllLiabilities] = useState(false);

    // Use values from executiveSummary and financialSnapshot
    const networth = formatCurrency(executiveSummary.net_worth || financialSnapshot.net_worth);

    // Asset breakdown from financialSnapshot.assets
    const assetsData = financialSnapshot.assets || {};
    const allAssetTypes = [
        { label: 'Real Estate', value: assetsData.real_estate || 0 },
        { label: 'Equity Funds', value: assetsData.equity_funds || 0 },
        { label: 'Dividend', value: assetsData.dividend || 0 },
        { label: 'Equity Investments', value: assetsData.equity_investments || 0 },
        { label: 'Debt Investments', value: assetsData.debt_investments || 0 },
        { label: 'Liquid Assets', value: assetsData.liquid_assets || 0 },
        { label: 'Other Assets', value: assetsData.other_assets || 0 },
    ];
    // Only show non-zero assets
    const assets = allAssetTypes.filter(a => a.value && a.value !== 0);
    const totalAssets = formatCurrency(assetsData.total_assets || 0);

    // Liabilities breakdown from financialSnapshot.liabilities
    const liabilitiesData = financialSnapshot.liabilities || {};
    const allLiabilityTypes = [
        { label: 'Real Estate', value: liabilitiesData.real_estate || 0 },
        { label: 'Equity Funds', value: liabilitiesData.equity_funds || 0 },
        { label: 'Dividend', value: liabilitiesData.dividend || 0 },
        { label: 'Home Loan', value: liabilitiesData.home_loan || 0 },
        { label: 'Personal Loans', value: liabilitiesData.personal_loans || 0 },
        { label: 'Credit Card Debt', value: liabilitiesData.credit_card_debt || 0 },
        { label: 'Other Liabilities', value: liabilitiesData.other_liabilities || 0 },
    ];
    // Only show non-zero liabilities
    const liabilities = allLiabilityTypes.filter(l => l.value && l.value !== 0);
    const totalLiabilities = formatCurrency(liabilitiesData.total_liabilities || 0);

    // Helper to get percent
    const getPercent = (value, total) => {
        if (!total || total === 0) return '0';
        return ((value / total) * 100).toFixed(0);
    };

    return (
        <SummaryCard title="Your total Networth">
            {/* Networth Header */}
            <div className="flex items-center justify-between mb-3">
                <h4 className="content-text text-gray-800 font-medium">Your total Networth</h4>
                <p className="text-xl font-bold text-gray-800">{networth}</p>
            </div>

            {/* Border separator */}
            <div className="border-b border-gray-300 my-3"></div>

            {/* Mobile Layout - Stacked */}
            <div className="block md:hidden">
                {/* Assets */}
                <div className="mb-3">
                    <div className="flex items-center justify-between mb-3">
                        <h5 className="content-text text-gray-800 font-medium">Assets</h5>
                        <p className="content-text font-bold text-gray-800">{totalAssets}</p>
                    </div>

                    <div className="space-y-3">
                        {(showAllAssets ? assets : assets.slice(0, 3)).map((asset, index) => (
                            <div key={asset.label} className="flex items-center justify-between">
                                <span className="content-text text-gray-500">{asset.label}</span>
                                <div className="text-right">
                                    <span className="content-text font-medium text-gray-800">
                                        {formatCurrency(asset.value)}
                                    </span>
                                    <span className="content-text text-gray-500 ml-1">
                                        ({getPercent(asset.value, assetsData.total_assets)}%)
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Separator */}
                <div className="border-b border-gray-300 my-3"></div>

                {/* Liabilities */}
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h5 className="content-text text-gray-800 font-medium">Liabilities</h5>
                        <p className="content-text font-bold text-gray-800">{totalLiabilities}</p>
                    </div>

                    <div className="space-y-3">
                        {(showAllLiabilities ? liabilities : liabilities.slice(0, 3)).map((liability, index) => (
                            <div key={liability.label} className="flex items-center justify-between">
                                <span className="content-text text-gray-500">{liability.label}</span>
                                <div className="text-right">
                                    <span className="content-text font-medium text-gray-800">
                                        {formatCurrency(liability.value)}
                                    </span>
                                    <span className="content-text text-gray-500 ml-1">
                                        ({getPercent(liability.value, liabilitiesData.total_liabilities)}%)
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Desktop Layout - Two Columns */}
            <div className="hidden md:block relative">
                <div className="grid grid-cols-2 gap-12">
                    {/* Assets Column */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h5 className="content-text text-gray-800 font-medium">Assets</h5>
                            <p className="content-text font-bold text-gray-800">{totalAssets}</p>
                        </div>

                        <div className="space-y-3">
                            {(showAllAssets ? assets : assets.slice(0, 3)).map((asset, index) => (
                                <div key={asset.label} className="flex items-center justify-between">
                                    <span className="content-text text-gray-500">{asset.label}</span>
                                    <div className="text-right">
                                        <span className="content-text font-medium text-gray-800">
                                            {formatCurrency(asset.value)}
                                        </span>
                                        <span className="content-text text-gray-500 ml-1">
                                            ({getPercent(asset.value, assetsData.total_assets)}%)
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Liabilities Column */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h5 className="content-text text-gray-800 font-medium">Liabilities</h5>
                            <p className="content-text font-bold text-gray-800">{totalLiabilities}</p>
                        </div>

                        <div className="space-y-3">
                            {(showAllLiabilities ? liabilities : liabilities.slice(0, 3)).map((liability, index) => (
                                <div key={liability.label} className="flex items-center justify-between">
                                    <span className="content-text text-gray-500">{liability.label}</span>
                                    <div className="text-right">
                                        <span className="content-text font-medium text-gray-800">
                                            {formatCurrency(liability.value)}
                                        </span>
                                        <span className="content-text text-gray-500 ml-1">
                                            ({getPercent(liability.value, liabilitiesData.total_liabilities)}%)
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Vertical Separator */}
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300 transform -translate-x-1/2"></div>
            </div>

            {/* View more button - centered */}
            {(assets.length > 3 || liabilities.length > 3) && (
                <div className="text-center mt-6">
                    <button
                        className="content-text text-gray-600 hover:text-gray-800 underline"
                        onClick={() => {
                            setShowAllAssets(!showAllAssets);
                            setShowAllLiabilities(!showAllLiabilities);
                        }}
                    >
                        {(showAllAssets || showAllLiabilities) ? 'Show less' : 'View more'}
                    </button>
                </div>
            )}
        </SummaryCard>
    );
}

export default YourNetworth;